import FlowchartLink from '../flowchart-link/flowchart-link.vue';
import FlowchartNode from '../flowchart-node/flowchart-node.vue';
import { Component, Prop, PropSync, Vue, Watch } from 'vue-property-decorator';

export function getOffsetRect(element: any) {
	const box = element.getBoundingClientRect();

	const scrollTop = window.pageYOffset;
	const scrollLeft = window.pageXOffset;

	const top = box.top + scrollTop;
	const left = box.left + scrollLeft;

	return { top: Math.round(top), left: Math.round(left) };
}

export function getMousePosition(element: any, event: any) {
	const mouseX = event.pageX || event.clientX + document.documentElement.scrollLeft;
	const mouseY = event.pageY || event.clientY + document.documentElement.scrollTop;

	const offset = getOffsetRect(element);
	const x = mouseX - offset.left;
	const y = mouseY - offset.top;

	return [x, y];
}

@Component({
	components: {
		FlowchartLink,
		FlowchartNode,
	},
})
export default class Flowchart extends Vue {
	public get nodeOptions() {
		return {
			centerY: this.localScene.centerY,
			centerX: this.localScene.centerX,
			scale: this.localScene.scale,
			offsetTop: this.rootDivOffset.top,
			offsetLeft: this.rootDivOffset.left,
			selected: this.action.selected,
		};
	}

	public get lines() {
		const lines = (this.localLinks || []).map((link: any) => {
			const fromNode = this.findNodeWithID(link.from);
			const toNode = this.findNodeWithID(link.to);

			const out = {
				id: link.id,
				start: [
					this.localScene.centerX + fromNode.x + 60,
					this.localScene.centerY + fromNode.y + 80,
				],
				end: [this.localScene.centerX + toNode.x + 60, this.localScene.centerY + toNode.y],
			};
			return out;
		});
		if (this.draggingLink) {
			const fromNode = this.findNodeWithID(this.draggingLink.from);

			const pos = [
				this.localScene.centerX + fromNode.x + 60,
				this.localScene.centerY + fromNode.y + 80,
			];

			lines.push({
				id: `${this.draggingLink.from},mouse`,
				start: pos,
				end: [this.draggingLink.mx || pos[0], this.draggingLink.my || pos[1]],
			});
		}
		return lines;
	}
	@PropSync('scene', { default: { centerX: 0, centerY: 0, scale: 1 } })
	public localScene?: any;

	@PropSync('nodes', { default: [] })
	public localNodes?: any[];

	@PropSync('links', { default: [] })
	public localLinks?: any[];

	@Prop({ default: 500 })
	public height?: number;

	@Prop()
	public disabled?: boolean;

	public action: any = {
		linking: false,
		dragging: false,
		scrolling: false,
		selected: 0,
	};

	public mouse: any = {
		x: 0,
		y: 0,
		lastX: 0,
		lastY: 0,
	};

	public rootDivOffset: any = {
		top: 0,
		left: 0,
	};

	public draggingLink: any = null;

	private nodeHeight = 105;

	public mounted() {
		// @ts-ignore
		this.rootDivOffset.top = this.$el ? this.$el.offsetTop : 0;
		// @ts-ignore
		this.rootDivOffset.left = this.$el ? this.$el.offsetLeft : 0;
	}

	public findNodeWithID(id: number) {
		return this.localNodes.find((item: any) => id === item.id);
	}

	public linkingStart(index: number) {
		this.action.linking = true;
		this.draggingLink = {
			from: index,
			mx: 0,
			my: 0,
		};
	}

	public linkingStop(index: number) {
		// add new Link
		if (this.localLinks !== undefined && this.draggingLink && this.draggingLink.from !== index) {
			// check link existence
			const existed = this.localLinks.find((link: any) => {
				return link.from === this.draggingLink.from && link.to === index;
			});
			if (!existed) {
				const newLink = {
					id: `${this.draggingLink.from},${index}`,
					from: this.draggingLink.from,
					to: index,
				};
				this.localLinks.push(newLink);
				this.$emit('linkAdded', newLink);
			}
		}
		this.draggingLink = null;
	}

	public linkDelete(id: number) {
		if (this.disabled || this.localLinks === undefined) {
			return;
		}
		const deletedLink = this.localLinks.find((item: any) => {
			return item.id === id;
		});
		if (deletedLink) {
			this.localLinks.splice(this.localLinks.indexOf(deletedLink), 1);
			this.$emit('linkBreak', deletedLink);
		}
	}

	public nodeSelected(id: number, e: any) {
		this.action.dragging = id;
		this.action.selected = id;
		this.$emit('nodeClick', id);
		this.mouse.lastX = e.pageX || e.clientX + document.documentElement.scrollLeft;
		this.mouse.lastY = e.pageY || e.clientY + document.documentElement.scrollTop;
	}

	public handleMove(e: any) {
		if (this.disabled) {
			e.preventDefault();
			return;
		}
		if (this.action.linking) {
			[this.mouse.x, this.mouse.y] = getMousePosition(this.$el, e);
			[this.draggingLink.mx, this.draggingLink.my] = [this.mouse.x, this.mouse.y];
		}
		if (this.action.dragging) {
			this.mouse.x = e.pageX || e.clientX + document.documentElement.scrollLeft;
			this.mouse.y = e.pageY || e.clientY + document.documentElement.scrollTop;
			const diffX = this.mouse.x - this.mouse.lastX;
			const diffY = this.mouse.y - this.mouse.lastY;

			this.mouse.lastX = this.mouse.x;
			this.mouse.lastY = this.mouse.y;
			this.moveSelectedNode(diffX, diffY);
		}
		if (this.action.scrolling) {
			[this.mouse.x, this.mouse.y] = getMousePosition(this.$el, e);
			const diffX = this.mouse.x - this.mouse.lastX;
			const diffY = this.mouse.y - this.mouse.lastY;

			this.mouse.lastX = this.mouse.x;
			this.mouse.lastY = this.mouse.y;

			this.localScene.centerX += diffX;
			this.localScene.centerY += diffY;

			// this.hasDragged = true
		}
	}

	public handleUp(e: any) {
		if (this.disabled) {
			e.preventDefault();
			return;
		}
		const target = e.target || e.srcElement;
		if (this.$el.contains(target)) {
			if (typeof target.className !== 'string' || target.className.indexOf('node-input') < 0) {
				this.draggingLink = null;
			}
			if (typeof target.className === 'string' && target.className.indexOf('node-delete') > -1) {
				// console.log('delete2', this.action.dragging);
				this.nodeDelete(this.action.dragging);
			}
		}
		if (this.action.dragging && this.localNodes !== undefined) {
			const index = this.localNodes.findIndex((item: any) => {
				return item.id === this.action.dragging;
			});
			const node = this.localNodes[index];

			const nearestToTop = [...this.localNodes]
				.filter(
					item =>
						item.id !== this.action.dragging &&
						Math.abs(this.distanceToTopConnector(node, item)) < 20,
				)
				.sort(
					(a: any, b: any) =>
						this.distanceToTopConnector(node, a) - this.distanceToTopConnector(node, b),
				);

			const nearestToBottom = [...this.localNodes]
				.filter(
					item =>
						item.id !== this.action.dragging &&
						Math.abs(this.distanceToBottomConnector(node, item)) < 20,
				)
				.sort(
					(a: any, b: any) =>
						this.distanceToBottomConnector(node, a) - this.distanceToBottomConnector(node, b),
				);

			if (node.connectedTop != null) {
				const ctn = this.findNode(node.connectedTop);
				if (ctn != null) {
					ctn.connectedBottom = null;
					this.updatePosition(ctn);
				}
			}
			if (node.connectedBottom != null) {
				const ctb = this.findNode(node.connectedBottom);
				if (ctb != null) {
					ctb.connectedTop = null;
					this.updatePosition(ctb);
				}
			}

			node.connectedTop = null;
			node.connectedBottom = null;

			if (
				nearestToBottom &&
				nearestToBottom.length > 0 &&
				nearestToTop &&
				nearestToTop.length > 0
			) {
				node.x = nearestToTop[0].x + (nearestToBottom[0].x - nearestToTop[0].x) / 2;
				node.y = nearestToTop[0].y + (nearestToBottom[0].y - nearestToTop[0].y) / 2;
				node.connectedTop = nearestToTop[0].id;
				node.connectedBottom = nearestToBottom[0].id;
			} else if (nearestToTop && nearestToTop.length > 0) {
				node.x = nearestToTop[0].x;
				node.y = nearestToTop[0].y + this.nodeHeight;
				node.connectedTop = nearestToTop[0].id;
			} else if (nearestToBottom && nearestToBottom.length > 0) {
				node.x = nearestToBottom[0].x;
				node.y = nearestToBottom[0].y - this.nodeHeight;
				node.connectedBottom = nearestToBottom[0].id;
			}
			if (node.connectedTop != null) {
				const ctn = this.findNode(node.connectedTop);
				if (ctn != null) {
					ctn.connectedBottom = node.id;
					this.updatePosition(ctn);
				}
			}
			if (node.connectedBottom != null) {
				const ctb = this.findNode(node.connectedBottom);
				if (ctb != null) {
					ctb.connectedTop = node.id;
					this.updatePosition(ctb);
				}
			}

			this.updatePosition(node);
		}
		this.action.linking = false;
		this.action.dragging = null;
		this.action.scrolling = false;
	}

	public handleDown(e: any) {
		if (this.disabled) {
			e.preventDefault();
			return;
		}
		const target = e.target || e.srcElement;
		if ((target === this.$el || target.matches('svg, svg *')) && e.which === 1) {
			this.action.scrolling = true;
			[this.mouse.lastX, this.mouse.lastY] = getMousePosition(this.$el, e);
			this.action.selected = null; // deselectAll
		}
		this.$emit('canvasClick', e);
	}

	public moveSelectedNode(dx: number, dy: number) {
		if (this.disabled || this.localNodes === undefined) {
			return;
		}
		const index = this.localNodes.findIndex((item: any) => {
			return item.id === this.action.dragging;
		});
		const left = this.localNodes[index].x + dx / this.localScene.scale;
		const top = this.localNodes[index].y + dy / this.localScene.scale;

		this.localNodes[index].x = left;
		this.localNodes[index].y = top;

		// this.$set(this.localScene.nodes, index, Object.assign(this.localScene.nodes[index], {
		//     x: left,
		//     y: top,
		// }));
	}

	public nodeDelete(id: number) {
		if (this.disabled || this.localNodes === undefined || this.localLinks === undefined) {
			return;
		}

		this.localNodes.splice(
			this.localNodes.findIndex(node => node.id === id),
			1,
		);
		this.localLinks.splice(
			this.localLinks.findIndex(link => link.from === id || link.to === id),
			1,
		);
		this.$emit('nodeDelete', id);
	}

	private updatePosition(node: any) {
		if (node.type === 'Water Usage') {
			const position =
				(node.connectedTop != null && node.connectedBottom != null) ||
				(node.connectedTop == null && node.connectedBottom == null)
					? 'inline'
					: node.connectedTop != null
					? 'outlet'
					: 'inlet';
			node.subLabel = `${node.subLabel.split(' ')[0]} ${position}`;
		}
	}

	private findNode(id: number) {
		return this.localNodes
			? this.localNodes[this.localNodes.findIndex(node => node.id === id)]
			: null;
	}

	private distanceToTopConnector(from: any, to: any) {
		return Math.hypot(from.x - to.x, from.y - to.y - this.nodeHeight);
	}

	private distanceToBottomConnector(from: any, to: any) {
		return Math.hypot(from.x - to.x, from.y - to.y + this.nodeHeight);
	}
}
